import React, { useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Modal,
  Platform,
  StyleSheet,
  Text,
  TextStyle,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import theme from '@/config/theme';
import type { AppUpdatePrompt } from '@/services/runtime/appUpdatePrompt';
import {
  markAppUpdatePromptShown,
  openAppUpdateDestination,
  snoozeAppUpdatePrompt,
} from '@/services/runtime/appUpdatePrompt';

type Props = {
  prompt: AppUpdatePrompt | null;
};

export const AppUpdatePromptModal: React.FC<Props> = ({ prompt }) => {
  const [isBusy, setIsBusy] = useState(false);
  const shownCampaignRef = useRef<string | null>(null);
  const isRequired = prompt?.mode === 'required';
  const { width } = useWindowDimensions();
  const isNarrow = width < 420;

  useEffect(() => {
    if (!prompt || shownCampaignRef.current === prompt.campaignId) return;
    shownCampaignRef.current = prompt.campaignId;
    void markAppUpdatePromptShown(prompt).catch(() => undefined);
  }, [prompt]);

  useEffect(() => {
    if (!prompt || typeof document === 'undefined') return;
    const preventEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
    };
    document.addEventListener('keydown', preventEscape, true);
    return () => document.removeEventListener('keydown', preventEscape, true);
  }, [prompt?.campaignId]);

  const handleLater = async () => {
    if (!prompt || isRequired || isBusy) return;
    setIsBusy(true);
    try {
      await snoozeAppUpdatePrompt(prompt);
    } finally {
      setIsBusy(false);
    }
  };

  const handleUpdate = async () => {
    if (!prompt || isBusy) return;
    setIsBusy(true);
    try {
      await openAppUpdateDestination(prompt);
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <Modal
      animationType="fade"
      onRequestClose={() => undefined}
      presentationStyle="overFullScreen"
      transparent
      visible={Boolean(prompt)}
    >
      {prompt && (
        <View style={styles.overlay} pointerEvents="auto" testID="app-update-prompt-overlay">
          <View
            accessibilityRole="alert"
            accessibilityViewIsModal
            style={styles.container}
          >
            <View style={styles.iconWrap}>
              <MaterialCommunityIcons
                name={isRequired ? 'cellphone-arrow-down' : 'cellphone-check'}
                size={30}
                color={theme.colors.primary.default}
              />
            </View>
            <Text style={styles.title}>{prompt.title}</Text>
            <Text style={styles.message}>{prompt.message}</Text>
            {prompt.targetVersion && (
              <Text style={styles.version}>Versão {prompt.targetVersion}</Text>
            )}
            <View style={[styles.buttonRow, isNarrow && styles.buttonColumn]}>
              {!isRequired && (
                <TouchableOpacity
                  accessibilityRole="button"
                  disabled={isBusy}
                  onPress={handleLater}
                  style={[styles.button, !isNarrow && styles.rowButton, styles.secondaryButton]}
                  testID="app-update-later"
                >
                  <Text style={[styles.buttonText, styles.secondaryButtonText]}>
                    Lembrar mais tarde
                  </Text>
                </TouchableOpacity>
              )}
              <TouchableOpacity
                accessibilityRole="button"
                disabled={isBusy}
                onPress={handleUpdate}
                style={[styles.button, !isNarrow && styles.rowButton, styles.primaryButton]}
                testID="app-update-now"
              >
                {isBusy ? (
                  <ActivityIndicator color={theme.colors.text.inverse} />
                ) : (
                  <Text style={[styles.buttonText, styles.primaryButtonText]}>
                    {Platform.OS === 'web' ? 'Atualizar página' : 'Atualizar agora'}
                  </Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      )}
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    ...StyleSheet.absoluteFill,
    alignItems: 'center',
    backgroundColor: 'rgba(17, 24, 39, 0.58)',
    justifyContent: 'center',
    padding: theme.spacing.lg,
    zIndex: theme.zIndex.modal,
  },
  container: {
    alignItems: 'center',
    backgroundColor: theme.colors.background.primary,
    borderRadius: theme.borderRadius.lg,
    maxWidth: 440,
    paddingHorizontal: theme.spacing.lg,
    paddingVertical: theme.spacing.xl,
    width: '100%',
    ...theme.shadows.lg,
  },
  iconWrap: {
    alignItems: 'center',
    backgroundColor: theme.colors.primary.light,
    borderRadius: 999,
    height: 58,
    justifyContent: 'center',
    marginBottom: theme.spacing.md,
    width: 58,
  },
  title: {
    color: theme.colors.text.primary,
    fontFamily: Platform.select({
      ios: theme.fontFamily?.medium,
      android: theme.fontFamily?.medium,
      web: theme.fontFamily?.medium,
    }),
    fontSize: theme.fontSizes.xl,
    fontWeight: theme.fontWeights.semibold as TextStyle['fontWeight'],
    marginBottom: theme.spacing.sm,
    textAlign: 'center',
  },
  message: {
    color: theme.colors.text.secondary,
    fontFamily: Platform.select({
      ios: theme.fontFamily?.regular,
      android: theme.fontFamily?.regular,
      web: theme.fontFamily?.regular,
    }),
    fontSize: theme.fontSizes.md,
    lineHeight: 22,
    marginBottom: theme.spacing.md,
    textAlign: 'center',
  },
  version: {
    color: theme.colors.text.tertiary,
    fontSize: theme.fontSizes.sm,
    marginBottom: theme.spacing.md,
  },
  buttonRow: {
    flexDirection: 'row',
    gap: theme.spacing.sm,
    marginTop: theme.spacing.sm,
    width: '100%',
  },
  buttonColumn: {
    flexDirection: 'column',
  },
  button: {
    alignItems: 'center',
    borderRadius: theme.borderRadius.md,
    justifyContent: 'center',
    minHeight: 48,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
  },
  rowButton: {
    flex: 1,
  },
  primaryButton: {
    backgroundColor: theme.colors.primary.default,
  },
  secondaryButton: {
    backgroundColor: theme.colors.background.secondary,
    borderColor: theme.colors.border.default,
    borderWidth: StyleSheet.hairlineWidth,
  },
  buttonText: {
    fontSize: theme.fontSizes.md,
    fontWeight: theme.fontWeights.medium as TextStyle['fontWeight'],
    textAlign: 'center',
  },
  primaryButtonText: {
    color: theme.colors.text.inverse,
  },
  secondaryButtonText: {
    color: theme.colors.text.primary,
  },
});
